import * as React from 'react';
import { X, Building2, Phone, Mail, Hash, MapPin, User, FileText, Calendar, DollarSign, Info } from 'lucide-react';
import type { Leaseholder } from '../../types/leaseholder';
import { useClickOutside } from '../../hooks/useClickOutside';

interface LeaseholderDetailsModalProps {
  leaseholder: Leaseholder;
  onClose: () => void;
}

export default function LeaseholderDetailsModal({ leaseholder, onClose }: LeaseholderDetailsModalProps) {
  const modalContentRef = React.useRef<HTMLDivElement>(null);
  useClickOutside(modalContentRef, onClose);

  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start md:items-center justify-center p-0 z-[100] overflow-y-auto"
    >
      <div 
        ref={modalContentRef}
        className="relative bg-white w-full md:max-w-3xl md:rounded-lg md:my-8 min-h-screen md:min-h-0 max-h-full md:max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 md:p-6 border-b bg-white">
          <h3 className="text-xl font-semibold text-gray-900">Leaseholder Details</h3>
          <button 
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content - with scrollable area */}
        <div className="p-4 md:p-6 space-y-6 overflow-y-auto flex-1">
          {/* Identification Information */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Identification Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">ID</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_id}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Name</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_name}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Position</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_position || 'Not specified'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">ABN</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_abn?.toString() || 'Not specified'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Indigenous Corporation Number</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_indcn?.toString() || 'Not specified'}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Contact Person</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_contact || 'Not specified'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Phone</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_phone || 'Not specified'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Email</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_email || 'Not specified'}
                </div>
              </div>
            </div>
          </div>

          {/* Physical Address */}
          <div className="border-b pb-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Physical Address</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Address Line 1</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_phy_add01 || 'Not specified'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Address Line 2</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_phy_add02 || 'Not specified'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Suburb</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_phy_sub || 'Not specified'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">State</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_phy_state || 'Not specified'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Postcode</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_phy_pcode || 'Not specified'}
                </div>
              </div>
            </div>
          </div>

          {/* Postal Address */}
          <div>
            <h4 className="text-lg font-medium text-gray-900 mb-4">Postal Address</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Address Line 1</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_post_add01 || 'Not specified'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Address Line 2</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_post_add02 || 'Not specified'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Suburb</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_post_sub || 'Not specified'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">State</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_post_state || 'Not specified'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Postcode</label>
                <div className="mt-1 p-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
                  {leaseholder.holder_pst_pcode || 'Not specified'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex justify-end gap-3 p-4 md:p-6 border-t bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 border border-gray-300 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
